import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale
);

interface SensorEntry {
  timestamp: string;
  value: number;
}

interface SensorData {
  [sensor: string]: SensorEntry[];
}

const DataGraph: React.FC = () => {
  const [sensorData, setSensorData] = useState<SensorData>({});
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = async () => {
    setLoading(true);
    const SUPABASE_URL = 'https://escjwccxbbdacgqykrqw.supabase.co';
    const SUPABASE_API_KEY =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVzY2p3Y2N4YmJkYWNncXlrcnF3Iiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NjE5NjIyMiwiZXhwIjoyMDYxNzcyMjIyfQ.azC7n_nGBFdaT98XBEUiJrsbdMyQTW9ynIKmB9dg_kk';
    const TABLE_NAME = 'loadcell_fixed';

    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/${TABLE_NAME}?order=timestamp.desc&limit=10`,
        {
          headers: {
            apikey: SUPABASE_API_KEY,
            Authorization: `Bearer ${SUPABASE_API_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await res.json();
      const tempSensorData: SensorData = {};

      data.reverse().forEach((row: any) => {
        const timestamp = row.timestamp || row.created_at;
        const parsed = JSON.parse(row.data);

        Object.entries(parsed).forEach(([sensor, value]: [string, any]) => {
          if (!tempSensorData[sensor]) {
            tempSensorData[sensor] = [];
          }
          tempSensorData[sensor].push({
            timestamp,
            value: parseFloat(value.toString()) || 0,
          });
        });
      });

      setSensorData(tempSensorData);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-4 md:p-10 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 uppercase tracking-wide">
          Traction Sensors
        </h1>
        <p className="text-gray-600 mt-2">
          Logging, Ocean-Based Utilities For Renewable Analytics
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-blue-500 border-gray-300"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Object.entries(sensorData)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([sensor, entries]) => {
              const dataPoints = entries.map((e) => ({
                x: new Date(e.timestamp),
                y: e.value,
              }));

              const latestValue = entries[entries.length - 1]?.value ?? 0;

              return (
                <div
                  key={sensor}
                  className="bg-white rounded-2xl shadow hover:shadow-lg transition-shadow duration-300 border border-gray-200 p-6 flex flex-col"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">
                      {sensor}
                    </h2>
                    <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full shadow-sm">
                      Latest: {latestValue.toFixed(2)} N
                    </span>
                  </div>
                  <div className="relative w-full h-[250px] md:h-[300px]">
                    <Line
                      data={{
                        datasets: [
                          {
                            label: sensor,
                            data: dataPoints,
                            borderColor: '#3b82f6',
                            backgroundColor: 'rgba(59, 130, 246, 0.2)',
                            fill: true,
                            tension: 0.4,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          
                            x: {
    type: 'category',
    labels: entries.map(e =>
      new Date(e.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    ),
    ticks: {
      color: '#6b7280',
      maxRotation: 60,
      minRotation: 45,
    },
    grid: { display: false },
  },
                          y: {
                            beginAtZero: true,
                            grid: { color: '#e5e7eb' },
                            ticks: {
                              color: '#6b7280',
                            },
                          },
                        },
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            mode: 'index',
                            intersect: false,
                          },
                        },
                        layout: {
                          padding: {
                            top: 10,
                            bottom: 20,
                            left: 10,
                            right: 10,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default DataGraph;
